"use client";
import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import { useTranslations } from "next-intl";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const IntegrationsLogos = () => {
  const t = useTranslations("integrations");
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });

  const categories = [
    {
      label: t("logos.categories.0.label"),
      items: t("logos.categories.0.items").split(", "),
    },
    {
      label: t("logos.categories.1.label"),
      items: t("logos.categories.1.items").split(", "),
    },
    {
      label: t("logos.categories.2.label"),
      items: t("logos.categories.2.items").split(", "),
    },
    {
      label: t("logos.categories.3.label"),
      items: t("logos.categories.3.items").split(", "),
    },
    {
      label: t("logos.categories.4.label"),
      items: t("logos.categories.4.items").split(", "),
    },
    {
      label: t("logos.categories.5.label"),
      items: t("logos.categories.5.items").split(", "),
    },
  ];

  const specialists = [
    {
      name: t("logos.specialists.0.title"),
      description: t("logos.specialists.0.description"),
    },
    {
      name: t("logos.specialists.1.title"),
      description: t("logos.specialists.1.description"),
    },
  ];

  return (
    <section ref={sectionRef} className="section pb-[60px] lg:pb-[140px] relative">
      <div
        className="absolute left-[-5%] top-[20%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.08)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-shape-pulse"
        />

      <div className="container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div className="text-center mb-[50px] lg:mb-[70px]" variants={itemVariants}>
            <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
              {t("logos.sectionTitle")}
            </h2>
            <p className="text-[15px] font-light leading-[1.7] text-foreground/90 max-w-[520px] mx-auto">
              {t("logos.sectionDescription")}
            </p>
          </motion.div>

          {/* Specialist platforms */}
          <div className="grid sm:grid-cols-2 gap-[14px] mb-[14px]">
            {specialists.map((item, i) => (
              <motion.div key={i} variants={itemVariants}>
                <AnimatedCard
                  className="rounded-[18px] lg:rounded-[22px] h-full"
                  circleClassName="w-[160px] h-[160px] blur-[90px]"
                  animationDelay={i * 0.07}
                >
                  <BorderWrapper
                    wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                    className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] relative overflow-hidden"
                  >
                    <div
                      className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/8 rounded-full blur-[70px] pointer-events-none animate-shape-pulse"
                      />
                    <div className="relative z-10">
                      <div className="flex items-center gap-[10px] mb-[16px]">
                        <div className="px-[10px] py-[4px] rounded-full bg-theme/20 border border-theme/30 text-[11px] font-medium text-theme uppercase tracking-[0.14em]">
                          {t("logos.specialist")}
                        </div>
                        <span className="text-[17px] lg:text-[19px] font-semibold">{item.name}</span>
                      </div>
                      <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                        {item.description}
                      </p>
                    </div>
                  </BorderWrapper>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>

        </motion.div>

          {/* Category grid — MOBILE STICKY STACK */}
          <div className="md:hidden max-w-[860px] mx-auto">
            {categories.map((cat, i) => (
              <div
                key={i}
                className="sticky mt-[2px]"
                style={{ top: `${20 + i * 64}px`, zIndex: 10 + i }}
              >
                <AnimatedCard
                  className="rounded-[16px]"
                  circleClassName="w-[100px] h-[100px] blur-[60px]"
                  animationDelay={i * 0.05}
                >
                  <BorderWrapper
                    wrapperClass="rounded-[16px]"
                    className="rounded-[16px] bg-gradient-to-b from-[#121124] to-[#0e0821] p-[22px] relative overflow-hidden"
                  >
                    <div className="text-[11px] font-medium text-theme/60 uppercase tracking-[0.16em] mb-[16px]">
                      {cat.label}
                    </div>
                    <div className="flex flex-wrap gap-[8px]">
                      {cat.items.map((item) => (
                        <span
                          key={item}
                          className="px-[12px] py-[6px] rounded-full text-[13px] font-light border border-white/10 bg-white/[0.03] text-foreground/90 hover:border-theme/30 hover:text-foreground/90 transition-colors duration-200 cursor-default"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </BorderWrapper>
                </AnimatedCard>
              </div>
            ))}
          </div>

          {/* Category grid — DESKTOP GRID */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-[14px]">
            {categories.map((cat, i) => (
              <AnimatedCard
                key={i}
                className="rounded-[16px] h-full"
                circleClassName="w-[100px] h-[100px] blur-[60px]"
                animationDelay={i * 0.05}
              >
                <BorderWrapper
                  wrapperClass="rounded-[16px] h-full"
                  className="rounded-[16px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[22px] lg:p-[26px] relative overflow-hidden"
                >
                  <div className="text-[11px] font-medium text-theme/60 uppercase tracking-[0.16em] mb-[16px]">
                    {cat.label}
                  </div>
                  <div className="flex flex-wrap gap-[8px]">
                    {cat.items.map((item) => (
                      <span
                        key={item}
                        className="px-[12px] py-[6px] rounded-full text-[13px] font-light border border-white/10 bg-white/[0.03] text-foreground/90 hover:border-theme/30 hover:text-foreground/90 transition-colors duration-200 cursor-default"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </BorderWrapper>
              </AnimatedCard>
            ))}
          </div>
      </div>
    </section>
  );
};

export default IntegrationsLogos;
